import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Save, Eye, FileText, Receipt } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";
import { printInvoice } from "@/lib/printInvoice";
import { printPosReceipt } from "@/lib/printPosReceipt";
import { printReport } from "@/lib/printReport";

const DEFAULT_A4 = {
  logo_url: "",
  header_title_en: "Tax Invoice",
  header_title_ar: "فاتورة ضريبية",
  business_name_en: "iSaloon",
  business_name_ar: "آي سالون",
  business_address_en: "",
  business_address_ar: "",
  tax_number: "",
  cr_number: "",
  phone: "",
  email: "",
  footer_text_en: "Thank you for your visit!",
  footer_text_ar: "شكراً لزيارتكم!",
  terms_en: "",
  terms_ar: "",
  show_customer: true,
  show_phone: true,
  show_loyalty: true,
  show_tax: true,
  show_discount: true,
  show_qr: false,
  primary_color: "#7A1F3D",
  page_size: "A4",
  language: "ar",
  default_print_format: "pos",
};

function harmonizeTemplate(data) {
  const merged = { ...DEFAULT_A4, ...data };
  const pos = { ...(merged.pos || {}) };
  const pick = (top, nested) => String(top ?? nested ?? "").trim();
  const phone = pick(merged.phone, pos.phone);
  const tax_number = pick(merged.tax_number, pos.tax_number);
  const cr_number = pick(merged.cr_number, pos.cr_number);
  const business_name_en = pick(merged.business_name_en, pos.business_name_en);
  const business_name_ar = pick(merged.business_name_ar, pos.business_name_ar);
  const business_address_en = pick(merged.business_address_en, pos.address_en);
  const business_address_ar = pick(merged.business_address_ar, pos.address_ar);
  return {
    ...merged,
    phone,
    tax_number,
    cr_number,
    business_name_en,
    business_name_ar,
    business_address_en,
    business_address_ar,
    pos: {
      ...pos,
      phone,
      tax_number,
      cr_number,
      business_name_en,
      business_name_ar,
      address_en: business_address_en,
      address_ar: business_address_ar,
    },
  };
}

const SAMPLE_INVOICE = (lang) => ({
  invoice_number: "INV-PREVIEW",
  created_at: new Date().toISOString(),
  customer_name: lang === "ar" ? "عميل تجريبي" : "Sample Customer",
  customer_phone: "+97455000000",
  payment_method: lang === "ar" ? "نقدي" : "cash",
  items: [
    { name: lang === "ar" ? "قص شعر" : "Haircut", name_ar: "قص شعر", name_en: "Haircut", qty: 1, unit_price: 80 },
    { name: lang === "ar" ? "صبغة" : "Hair Color", name_ar: "صبغة", name_en: "Hair Color", qty: 1, unit_price: 250 },
    { name: lang === "ar" ? "كريم مرطب" : "Moisturizer", name_ar: "كريم مرطب", name_en: "Moisturizer", qty: 2, unit_price: 75 },
  ],
  subtotal: 480,
  discount: 30,
  tax: 67.5,
  total: 517.5,
  loyalty_earned: 51,
  loyalty_redeemed: 0,
  loyalty_value_applied: 0,
  coupon_code: "WELCOME10",
  coupon_discount: 0,
});

export default function InvoiceDesign() {
  const { lang } = useLanguage();
  const [tpl, setTpl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("a4"); // a4 | pos

  const load = async () => {
    try {
      const r = await api.get("/invoice-template");
      setTpl(harmonizeTemplate(r.data));
    } catch {
      setTpl(harmonizeTemplate({}));
    }
  };
  useEffect(() => { load(); }, []);

  const setF = (k, v) => setTpl((c) => ({ ...c, [k]: v }));
  const setPos = (k, v) => setTpl((c) => ({ ...c, pos: { ...(c.pos || {}), [k]: v } }));

  // Business contact fields are shared between A4 and POS receipts — keep both
  // copies in sync so changing one tab updates what actually prints.
  const setSharedContact = (patch) => {
    setTpl((c) => {
      const posPatch = { ...patch };
      if ("business_address_en" in patch) {
        posPatch.address_en = patch.business_address_en;
        delete posPatch.business_address_en;
      }
      if ("business_address_ar" in patch) {
        posPatch.address_ar = patch.business_address_ar;
        delete posPatch.business_address_ar;
      }
      const next = {
        ...c,
        ...patch,
        pos: { ...(c.pos || {}), ...posPatch },
      };
      return harmonizeTemplate(next);
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = harmonizeTemplate(tpl);
      await api.put("/invoice-template", payload);
      setTpl(payload);
      toast.success(lang === "ar" ? "تم حفظ التصميم" : "Template saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || (lang === "ar" ? "فشل الحفظ" : "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const previewA4 = () => printInvoice(SAMPLE_INVOICE(lang), harmonizeTemplate(tpl), lang);
  const previewPos = () => printPosReceipt(SAMPLE_INVOICE(lang), harmonizeTemplate(tpl), lang);
  // Preview a sample management report (Revenue snapshot) using the same
  // template the tenant just configured. Validates that report-style
  // prints pick up the logo, business name, contact strip, primary color
  // and footer text exactly like invoices do.
  const previewReport = () => {
    const isAr = lang === "ar";
    printReport({
      title: isAr ? "معاينة تقرير" : "Report Preview",
      subtitle: isAr ? "مثال على تقرير المبيعات اليومي" : "Sample daily sales report",
      lang,
      template: tpl,
      meta: [
        { label: isAr ? "الفترة" : "Period", value: new Date().toISOString().slice(0, 10) },
        { label: isAr ? "الفرع" : "Branch", value: isAr ? "الفرع الرئيسي" : "Main branch" },
        { label: isAr ? "عدد الفواتير" : "Invoices", value: "12" },
      ],
      columns: [
        isAr ? "الوقت" : "Time",
        isAr ? "رقم الفاتورة" : "Invoice #",
        isAr ? "العميل" : "Customer",
        isAr ? "الإجمالي" : "Total",
      ],
      rows: [
        ["09:15", "INV-1001", isAr ? "نورة" : "Noura", { value: "320.00", className: "num pos" }],
        ["10:30", "INV-1002", isAr ? "هند" : "Hind",   { value: "180.00", className: "num pos" }],
        ["12:05", "INV-1003", isAr ? "سارة" : "Sara",  { value: "517.50", className: "num pos" }],
        ["14:20", "INV-1004", isAr ? "ريم" : "Reem",   { value: "245.00", className: "num pos" }],
      ],
      totals: [
        { label: isAr ? "الإجمالي" : "Total", value: "1,262.50", className: "gold" },
        { label: isAr ? "متوسط الفاتورة" : "Avg invoice", value: "315.62" },
      ],
    });
  };

  if (!tpl) return <div className="p-6 text-[var(--text-muted)]">{lang === "ar" ? "تحميل..." : "Loading..."}</div>;

  const inputCls = "w-full px-3 py-2 rounded-md bg-[var(--surface-2)] border border-[var(--border)] text-[var(--text)] focus:border-[var(--gold)] outline-none";
  const labelCls = "text-xs text-[var(--text-muted)] mb-1 block";

  const pos = tpl.pos || {};
  const businessPhone = tpl.phone || "";
  const businessTax = tpl.tax_number || "";
  const businessCr = tpl.cr_number || "";
  const businessNameEn = tpl.business_name_en || "";
  const businessNameAr = tpl.business_name_ar || "";
  const businessAddressEn = tpl.business_address_en || "";
  const businessAddressAr = tpl.business_address_ar || "";

  return (
    <div className="space-y-6" data-testid="invoice-design-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-heading gold-text flex items-center gap-3">
            <FileText size={28} />
            {lang === "ar" ? "هوية الطباعة" : "Print Identity"}
          </h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            {lang === "ar"
              ? "هذا القالب يُستخدم لطباعة جميع الفواتير، الإيصالات، السندات، كشوف الحسابات، والتقارير الإدارية."
              : "This template is used for printing all invoices, receipts, vouchers, account statements, and management reports."}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={tab === "a4" ? previewA4 : previewPos} data-testid="invoice-preview-btn" className="btn-rose-outline px-4 py-2 rounded-md text-sm flex items-center gap-2">
            <Eye size={16} /> {lang === "ar" ? "معاينة الفاتورة" : "Preview Invoice"}
          </button>
          <button onClick={previewReport} data-testid="report-preview-btn" className="btn-rose-outline px-4 py-2 rounded-md text-sm flex items-center gap-2">
            <Eye size={16} /> {lang === "ar" ? "معاينة التقرير" : "Preview Report"}
          </button>
          <button onClick={save} disabled={saving} data-testid="invoice-save-btn" className="btn-gold px-4 py-2 rounded-md text-sm flex items-center gap-2">
            <Save size={16} /> {saving ? (lang === "ar" ? "جارٍ الحفظ..." : "Saving...") : (lang === "ar" ? "حفظ" : "Save")}
          </button>
        </div>
      </div>

      {/* Default print format selector */}
      <div className="card-surface p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-heading text-sm gold-text">{lang === "ar" ? "النوع الافتراضي للطباعة من POS" : "Default print format from POS"}</h3>
          <p className="text-xs text-[var(--text-muted)] mt-0.5">{lang === "ar" ? "هذا النوع سيتم اختياره تلقائياً عند الدفع" : "This format will be selected automatically at checkout"}</p>
        </div>
        <div className="inline-flex rounded-md border border-[var(--border)] bg-[var(--surface-2)] p-1" data-testid="default-format-toggle">
          <button onClick={() => setF("default_print_format", "pos")} data-testid="default-format-pos" className={`px-4 py-1.5 rounded text-sm font-medium transition flex items-center gap-1.5 ${tpl.default_print_format === "pos" ? "bg-[var(--gold)] text-black" : "text-[var(--text-muted)]"}`}>
            <Receipt size={14}/>{lang === "ar" ? "إيصال POS" : "POS Receipt"}
          </button>
          <button onClick={() => setF("default_print_format", "a4")} data-testid="default-format-a4" className={`px-4 py-1.5 rounded text-sm font-medium transition flex items-center gap-1.5 ${tpl.default_print_format === "a4" ? "bg-[var(--gold)] text-black" : "text-[var(--text-muted)]"}`}>
            <FileText size={14}/>{lang === "ar" ? "فاتورة A4" : "A4 Invoice"}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="inline-flex rounded-md border border-[var(--border)] bg-[var(--surface-2)] p-1" data-testid="design-tabs">
        <button onClick={() => setTab("a4")} data-testid="tab-a4" className={`px-5 py-2 rounded text-sm font-medium transition flex items-center gap-2 ${tab === "a4" ? "bg-[var(--gold)] text-black" : "text-[var(--text-muted)]"}`}>
          <FileText size={14}/>{lang === "ar" ? "تصميم A4" : "A4 Design"}
        </button>
        <button onClick={() => setTab("pos")} data-testid="tab-pos" className={`px-5 py-2 rounded text-sm font-medium transition flex items-center gap-2 ${tab === "pos" ? "bg-[var(--gold)] text-black" : "text-[var(--text-muted)]"}`}>
          <Receipt size={14}/>{lang === "ar" ? "تصميم إيصال POS" : "POS Receipt Design"}
        </button>
      </div>

      {tab === "a4" && (
        <div className="grid lg:grid-cols-3 gap-6" data-testid="a4-design-section">
          <div className="lg:col-span-2 space-y-6">
            <div className="card-surface p-5 space-y-4">
              <h2 className="font-heading text-xl text-[var(--gold)]">{lang === "ar" ? "الهيدر" : "Header"}</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className={labelCls}>{lang === "ar" ? "شعار الفاتورة" : "Invoice Logo"}</label>
                  <ImageUpload value={tpl.logo_url} onChange={(u) => setF("logo_url", u)} recommendedSize="200×200" category="invoice" aspect="aspect-square" testIdPrefix="invoice-logo" lang={lang} />
                </div>
                <div><label className={labelCls}>{lang === "ar" ? "عنوان المستند (EN)" : "Doc Title (EN)"}</label><input className={inputCls} value={tpl.header_title_en} onChange={(e) => setF("header_title_en", e.target.value)} data-testid="invoice-header-title-en"/></div>
                <div><label className={labelCls}>{lang === "ar" ? "عنوان المستند (AR)" : "Doc Title (AR)"}</label><input className={inputCls} value={tpl.header_title_ar} onChange={(e) => setF("header_title_ar", e.target.value)} data-testid="invoice-header-title-ar"/></div>
                <div><label className={labelCls}>{lang === "ar" ? "اسم النشاط (EN)" : "Business Name (EN)"}</label><input className={inputCls} value={tpl.business_name_en} onChange={(e) => setSharedContact({ business_name_en: e.target.value })} /></div>
                <div><label className={labelCls}>{lang === "ar" ? "اسم النشاط (AR)" : "Business Name (AR)"}</label><input className={inputCls} value={tpl.business_name_ar} onChange={(e) => setSharedContact({ business_name_ar: e.target.value })} /></div>
                <div className="md:col-span-2"><label className={labelCls}>{lang === "ar" ? "العنوان (AR)" : "Address (AR)"}</label><input className={inputCls} value={tpl.business_address_ar} onChange={(e) => setSharedContact({ business_address_ar: e.target.value })} /></div>
                <div className="md:col-span-2"><label className={labelCls}>{lang === "ar" ? "العنوان (EN)" : "Address (EN)"}</label><input className={inputCls} value={tpl.business_address_en} onChange={(e) => setSharedContact({ business_address_en: e.target.value })} /></div>
                <div><label className={labelCls}>{lang === "ar" ? "رقم الهاتف" : "Phone"}</label><input className={inputCls} dir="ltr" value={businessPhone} onChange={(e) => setSharedContact({ phone: e.target.value })} data-testid="invoice-phone" /></div>
                <div><label className={labelCls}>{lang === "ar" ? "البريد الإلكتروني" : "Email"}</label><input className={inputCls} value={tpl.email} onChange={(e) => setF("email", e.target.value)} /></div>
                <div><label className={labelCls}>{lang === "ar" ? "الرقم الضريبي (VAT)" : "VAT Number"}</label><input className={inputCls} value={tpl.tax_number} onChange={(e) => setSharedContact({ tax_number: e.target.value })} /></div>
                <div><label className={labelCls}>{lang === "ar" ? "السجل التجاري" : "CR Number"}</label><input className={inputCls} value={tpl.cr_number} onChange={(e) => setSharedContact({ cr_number: e.target.value })} /></div>
              </div>
            </div>
            <div className="card-surface p-5 space-y-4">
              <h2 className="font-heading text-xl text-[var(--gold)]">{lang === "ar" ? "الفوتر والشروط" : "Footer & Terms"}</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div><label className={labelCls}>{lang === "ar" ? "نص الفوتر (AR)" : "Footer Text (AR)"}</label><input className={inputCls} value={tpl.footer_text_ar} onChange={(e) => setF("footer_text_ar", e.target.value)} data-testid="invoice-footer-ar"/></div>
                <div><label className={labelCls}>{lang === "ar" ? "نص الفوتر (EN)" : "Footer Text (EN)"}</label><input className={inputCls} value={tpl.footer_text_en} onChange={(e) => setF("footer_text_en", e.target.value)} /></div>
                <div className="md:col-span-2"><label className={labelCls}>{lang === "ar" ? "الشروط والأحكام (AR)" : "Terms (AR)"}</label><textarea rows={3} className={inputCls} value={tpl.terms_ar} onChange={(e) => setF("terms_ar", e.target.value)} /></div>
                <div className="md:col-span-2"><label className={labelCls}>{lang === "ar" ? "الشروط والأحكام (EN)" : "Terms (EN)"}</label><textarea rows={3} className={inputCls} value={tpl.terms_en} onChange={(e) => setF("terms_en", e.target.value)} /></div>
              </div>
            </div>
          </div>
          <div className="space-y-6">
            <div className="card-surface p-5 space-y-3">
              <h2 className="font-heading text-xl text-[var(--gold)]">{lang === "ar" ? "العناصر الظاهرة" : "Visible Fields"}</h2>
              {[
                ["show_customer", lang === "ar" ? "بيانات العميل" : "Customer info"],
                ["show_phone", lang === "ar" ? "هاتف العميل" : "Customer phone"],
                ["show_loyalty", lang === "ar" ? "نقاط الولاء" : "Loyalty points"],
                ["show_tax", lang === "ar" ? "ضريبة القيمة المضافة" : "VAT (tax) line"],
                ["show_discount", lang === "ar" ? "الخصم" : "Discount line"],
              ].map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-[var(--text)]">
                  <input type="checkbox" data-testid={`tpl-${k}`} checked={!!tpl[k]} onChange={(e) => setF(k, e.target.checked)} />
                  <span>{label}</span>
                </label>
              ))}
            </div>
            <div className="card-surface p-5 space-y-4">
              <h2 className="font-heading text-xl text-[var(--gold)]">{lang === "ar" ? "المظهر" : "Appearance"}</h2>
              <div>
                <label className={labelCls}>{lang === "ar" ? "اللون الرئيسي" : "Primary Color"}</label>
                <div className="flex items-center gap-2">
                  <input type="color" className="w-16 h-10 rounded cursor-pointer bg-[var(--surface-2)] border border-[var(--border)]" value={tpl.primary_color} onChange={(e) => setF("primary_color", e.target.value)} />
                  <input className={inputCls} value={tpl.primary_color} onChange={(e) => setF("primary_color", e.target.value)} />
                </div>
              </div>
              <div>
                <label className={labelCls}>{lang === "ar" ? "لغة الفاتورة الافتراضية" : "Default invoice language"}</label>
                <select className={inputCls} value={tpl.language} onChange={(e) => setF("language", e.target.value)}>
                  <option value="ar">العربية</option>
                  <option value="en">English</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>{lang === "ar" ? "حجم الورق" : "Page size"}</label>
                <select className={inputCls} value={tpl.page_size} onChange={(e) => setF("page_size", e.target.value)}>
                  <option value="A4">A4</option>
                  <option value="A5">A5</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "pos" && (
        <div className="grid lg:grid-cols-3 gap-6" data-testid="pos-design-section">
          <div className="lg:col-span-2 space-y-6">
            <div className="card-surface p-5 space-y-4">
              <h2 className="font-heading text-xl text-[var(--gold)] flex items-center gap-2"><Receipt size={20}/>{lang === "ar" ? "هيدر الإيصال" : "Receipt Header"}</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className={labelCls}>{lang === "ar" ? "شعار الإيصال (يُنصح: 200×80)" : "Receipt Logo (recommended: 200×80)"}</label>
                  <ImageUpload value={pos.logo_url} onChange={(u) => setPos("logo_url", u)} recommendedSize="200×80" category="invoice" aspect="aspect-[3/1]" testIdPrefix="pos-logo" lang={lang} />
                </div>
                <div><label className={labelCls}>{lang === "ar" ? "عنوان الإيصال (AR)" : "Receipt Title (AR)"}</label><input className={inputCls} value={pos.header_title_ar || ""} onChange={(e) => setPos("header_title_ar", e.target.value)} data-testid="pos-header-title-ar"/></div>
                <div><label className={labelCls}>{lang === "ar" ? "عنوان الإيصال (EN)" : "Receipt Title (EN)"}</label><input className={inputCls} value={pos.header_title_en || ""} onChange={(e) => setPos("header_title_en", e.target.value)} data-testid="pos-header-title-en"/></div>
                <div><label className={labelCls}>{lang === "ar" ? "اسم النشاط (AR)" : "Business Name (AR)"}</label><input className={inputCls} value={businessNameAr} onChange={(e) => setSharedContact({ business_name_ar: e.target.value })} /></div>
                <div><label className={labelCls}>{lang === "ar" ? "اسم النشاط (EN)" : "Business Name (EN)"}</label><input className={inputCls} value={businessNameEn} onChange={(e) => setSharedContact({ business_name_en: e.target.value })} /></div>
                <div className="md:col-span-2"><label className={labelCls}>{lang === "ar" ? "العنوان (AR)" : "Address (AR)"}</label><input className={inputCls} value={businessAddressAr} onChange={(e) => setSharedContact({ business_address_ar: e.target.value })} /></div>
                <div className="md:col-span-2"><label className={labelCls}>{lang === "ar" ? "العنوان (EN)" : "Address (EN)"}</label><input className={inputCls} value={businessAddressEn} onChange={(e) => setSharedContact({ business_address_en: e.target.value })} /></div>
                <div><label className={labelCls}>{lang === "ar" ? "رقم الهاتف" : "Phone"}</label><input className={inputCls} dir="ltr" value={businessPhone} onChange={(e) => setSharedContact({ phone: e.target.value })} data-testid="pos-phone" /></div>
                <div><label className={labelCls}>{lang === "ar" ? "الرقم الضريبي" : "VAT Number"}</label><input className={inputCls} value={businessTax} onChange={(e) => setSharedContact({ tax_number: e.target.value })} /></div>
                <div><label className={labelCls}>{lang === "ar" ? "السجل التجاري" : "CR Number"}</label><input className={inputCls} value={businessCr} onChange={(e) => setSharedContact({ cr_number: e.target.value })} /></div>
              </div>
            </div>

            <div className="card-surface p-5 space-y-4">
              <h2 className="font-heading text-xl text-[var(--gold)]">{lang === "ar" ? "فوتر الإيصال" : "Receipt Footer"}</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div><label className={labelCls}>{lang === "ar" ? "نص الشكر (AR)" : "Thanks Text (AR)"}</label><input className={inputCls} value={pos.footer_text_ar || ""} onChange={(e) => setPos("footer_text_ar", e.target.value)} data-testid="pos-footer-ar"/></div>
                <div><label className={labelCls}>{lang === "ar" ? "نص الشكر (EN)" : "Thanks Text (EN)"}</label><input className={inputCls} value={pos.footer_text_en || ""} onChange={(e) => setPos("footer_text_en", e.target.value)} /></div>
                <div className="md:col-span-2"><label className={labelCls}>{lang === "ar" ? "ملاحظة إضافية (AR)" : "Footer Note (AR)"}</label><input className={inputCls} value={pos.footer_note_ar || ""} onChange={(e) => setPos("footer_note_ar", e.target.value)} /></div>
                <div className="md:col-span-2"><label className={labelCls}>{lang === "ar" ? "ملاحظة إضافية (EN)" : "Footer Note (EN)"}</label><input className={inputCls} value={pos.footer_note_en || ""} onChange={(e) => setPos("footer_note_en", e.target.value)} /></div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="card-surface p-5 space-y-3">
              <h2 className="font-heading text-xl text-[var(--gold)]">{lang === "ar" ? "الإعدادات" : "Settings"}</h2>
              <div>
                <label className={labelCls}>{lang === "ar" ? "عرض الورق (mm)" : "Paper Width (mm)"}</label>
                <select className={inputCls} value={pos.paper_width_mm || 80} onChange={(e) => setPos("paper_width_mm", parseInt(e.target.value))} data-testid="pos-paper-width">
                  <option value={58}>58 mm</option>
                  <option value={80}>80 mm (Standard)</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>{lang === "ar" ? "لغة الإيصال" : "Receipt Language"}</label>
                <select className={inputCls} value={pos.language || "ar"} onChange={(e) => setPos("language", e.target.value)}>
                  <option value="ar">العربية</option>
                  <option value="en">English</option>
                </select>
              </div>
            </div>

            <div className="card-surface p-5 space-y-3">
              <h2 className="font-heading text-xl text-[var(--gold)]">{lang === "ar" ? "العناصر الظاهرة" : "Visible Fields"}</h2>
              {[
                ["show_logo", lang === "ar" ? "إظهار الشعار" : "Show logo"],
                ["show_customer", lang === "ar" ? "بيانات العميل" : "Customer info"],
                ["show_phone", lang === "ar" ? "هاتف العميل" : "Customer phone"],
                ["show_loyalty", lang === "ar" ? "نقاط الولاء" : "Loyalty points"],
                ["show_tax", lang === "ar" ? "الضريبة" : "VAT line"],
                ["show_discount", lang === "ar" ? "الخصم" : "Discount line"],
                ["show_qr", lang === "ar" ? "QR للإيصال" : "QR code"],
              ].map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-[var(--text)]">
                  <input type="checkbox" data-testid={`pos-${k}`} checked={!!pos[k]} onChange={(e) => setPos(k, e.target.checked)} />
                  <span>{label}</span>
                </label>
              ))}
            </div>

            {/* Mini live preview */}
            <div className="card-surface p-3">
              <div className="text-xs text-[var(--text-muted)] mb-2 px-2 pt-2">{lang === "ar" ? "معاينة مصغّرة" : "Mini preview"}</div>
              <div className="bg-white text-black rounded p-3 text-[10px] leading-tight font-mono mx-auto" style={{ width: "75mm", direction: pos.language === "ar" ? "rtl" : "ltr" }} data-testid="pos-mini-preview">
                <div className="text-center">
                  {pos.show_logo && pos.logo_url && <img src={pos.logo_url.startsWith("http") ? pos.logo_url : pos.logo_url} alt="" style={{ maxHeight: 30, margin: "0 auto 4px" }} />}
                  <div className="font-bold text-[12px]">{pos.language === "ar" ? businessNameAr : businessNameEn}</div>
                  {(pos.language === "ar" ? businessAddressAr : businessAddressEn) && (
                    <div>{pos.language === "ar" ? businessAddressAr : businessAddressEn}</div>
                  )}
                  {businessPhone && (
                    <div dir="ltr" data-testid="pos-mini-preview-phone">
                      {pos.language === "ar" ? "هاتف" : "Tel"}: {businessPhone}
                    </div>
                  )}
                  <div className="font-bold mt-1">{pos.language === "ar" ? pos.header_title_ar : pos.header_title_en}</div>
                </div>
                <div className="border-t border-dashed border-black my-1.5"></div>
                <div className="flex justify-between"><span>{pos.language === "ar" ? "البند" : "Item"}</span><span>{pos.language === "ar" ? "المبلغ" : "Amt"}</span></div>
                <div className="flex justify-between"><span>{pos.language === "ar" ? "قص شعر" : "Haircut"}</span><span>80.00</span></div>
                <div className="border-t border-dashed border-black my-1.5"></div>
                <div className="flex justify-between font-bold text-[11px]"><span>{pos.language === "ar" ? "الإجمالي" : "TOTAL"}</span><span>80.00</span></div>
                <div className="text-center mt-2 font-bold">{pos.language === "ar" ? pos.footer_text_ar : pos.footer_text_en}</div>
                {(pos.language === "ar" ? pos.footer_note_ar : pos.footer_note_en) && <div className="text-center text-[9px] text-stone-700">{pos.language === "ar" ? pos.footer_note_ar : pos.footer_note_en}</div>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
